import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { extraDiceCost, rerollCost, successProbability } from "@roguelife/engine";
import { useGame } from "../store";
import { SuccessGauge } from "./SuccessGauge";
import { Die, type DiePhase } from "./Dice";
import { useReducedMotion } from "../hooks/useReducedMotion";
import type { PointBreakdownItem } from "../types";

const OUTCOME_TEXT: Record<number, { text: string; cls: string }> = {
  3: { text: "大成功", cls: "crit-win" },
  2: { text: "成功", cls: "win" },
  1: { text: "失败", cls: "lose" },
  0: { text: "大失败", cls: "crit-lose" },
};

export function CheckPanel() {
  const {
    view,
    selectedAction,
    extraDice,
    setExtraDice,
    confirmAction,
    rollDice,
    revealRollResult,
    cont,
    busy,
    clientRoll,
    rollAnimationId,
    rollRevealed,
  } = useGame();
  const reducedMotion = useReducedMotion();
  const pending = view?.pending;
  const numDice = pending?.numDice ?? 2 + extraDice;
  const luck = view?.player.luck ?? 0;
  const autoRollRef = useRef<string | null>(null);
  const autoContinueRef = useRef<string | null>(null);
  const autoRollKey =
    pending?.needsCheck && !pending.rolled
      ? `${pending.actionId}:${pending.currentPoints}:${pending.requiredPoints}:${pending.numDice}`
      : null;
  const autoContinueKey = pending && !pending.needsCheck ? pending.actionId : null;
  const rolled = pending?.rolled;
  const tone =
    rolled?.outcome === 3 || rolled?.outcome === 2
      ? "win"
      : rolled
        ? "lose"
        : undefined;
  const settlingNow = !!rolled && !!rollAnimationId && !rollRevealed;
  const rollAnimating = !!clientRoll || settlingNow;
  const resultVisible = !!rolled && !clientRoll && (!rollAnimationId || rollRevealed);
  const activeDiceValues = clientRoll
    ? rollingDiceValues(clientRoll.numDice, clientRoll.id)
    : rolled?.dice ?? rollingDiceValues(numDice, 0);
  const dicePhase: DiePhase = clientRoll ? "rolling" : settlingNow ? "settling" : "settled";
  const rollKey = clientRoll?.id ?? rollAnimationId ?? "static";

  useEffect(() => {
    if (!autoRollKey || busy || clientRoll) return;
    if (autoRollRef.current === autoRollKey) return;
    autoRollRef.current = autoRollKey;
    void rollDice();
  }, [autoRollKey, busy, clientRoll, rollDice]);

  useEffect(() => {
    if (!autoContinueKey || busy || clientRoll) return;
    if (autoContinueRef.current === autoContinueKey) return;
    autoContinueRef.current = autoContinueKey;
    void cont();
  }, [autoContinueKey, busy, clientRoll, cont]);

  useEffect(() => {
    if (!settlingNow || !rollAnimationId) return;
    const timer = window.setTimeout(
      () => revealRollResult(rollAnimationId),
      reducedMotion ? 120 : 1450,
    );
    return () => window.clearTimeout(timer);
  }, [settlingNow, rollAnimationId, revealRollResult, reducedMotion]);

  if (!pending && !selectedAction) return null;

  if (!pending && selectedAction) {
    if (!selectedAction.needsCheck) {
      return (
        <div className="check-panel no-check preview">
          <div className="check-head">掂量去路</div>
          <div className="check-info">
            <div>行动：{selectedAction.name}</div>
            <div>此路无须掷骰</div>
          </div>
          <div className="check-actions">
            <button className="btn primary" disabled={busy} onClick={() => confirmAction()}>
              踏上此路
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={`check-panel ${clientRoll ? "rolling" : "armed"} preview`}>
        <div className="check-sigil" aria-hidden="true" />
        <div className="check-head">掂量胜算</div>
        <div className="check-grid">
          <SuccessGauge
            current={selectedAction.currentPoints}
            required={selectedAction.requiredPoints}
            numDice={numDice}
          />
          <div className="check-info">
            <div>行动：{selectedAction.name}</div>
            <div>命数门槛：{selectedAction.requiredPoints}</div>
            <div>当前助力：{selectedAction.currentPoints}</div>
            <PointSummary items={selectedAction.pointBreakdown} />
            <PointBreakdown items={selectedAction.pointBreakdown} />
            <div className="luck-line">
              气运 <b className="luck">⚜ {luck}</b>
            </div>
            <ExtraDiceControl
              extraDice={extraDice}
              luck={luck}
              currentPoints={selectedAction.currentPoints}
              requiredPoints={selectedAction.requiredPoints}
              maxRerolls={view?.difficulty.maxRerollsPerCheck ?? null}
              disabled={busy}
              onChange={setExtraDice}
            />
          </div>
        </div>
        {clientRoll && (
          <DiceStrip
            values={activeDiceValues}
            phase="rolling"
            rollKey={rollKey}
          >
            <div className="roll-suspense">骰子已掷出...</div>
          </DiceStrip>
        )}
        <div className="check-actions">
          {clientRoll ? (
            <span className="reroll-hint">等待命运回声...</span>
          ) : (
            <button className="btn primary" disabled={busy} onClick={() => confirmAction()}>
              掷下命数
            </button>
          )}
        </div>
      </div>
    );
  }

  // No check required — continue automatically.
  if (pending && !pending.needsCheck) {
    return (
      <div className="check-panel no-check">
        <div className="check-head">去路已定</div>
        <span className="reroll-hint">脚步向前...</span>
      </div>
    );
  }

  if (!pending) return null;

  return (
    <div className={`check-panel ${rollAnimating ? "rolling" : rolled && resultVisible ? `rolled ${tone}` : "armed"}`}>
      <div className="check-sigil" aria-hidden="true" />
      <div className="check-grid">
        <SuccessGauge
          current={pending.currentPoints}
          required={pending.requiredPoints}
          numDice={numDice}
        />
        <div className="check-info">
          <div>命数门槛：{pending.requiredPoints}</div>
          <div>当前助力：{pending.currentPoints}</div>
          <PointSummary items={pending.pointBreakdown} />
          <PointBreakdown items={pending.pointBreakdown} />
          <div className="luck-line">
            气运 <b className="luck">⚜ {luck}</b>
          </div>
          <div className="extra-die locked">额外骰子：{Math.max(0, numDice - 2)} 颗</div>
        </div>
      </div>

      {(rolled || clientRoll) && (
        <DiceStrip
          values={activeDiceValues}
          phase={dicePhase}
          tone={resultVisible ? tone : undefined}
          rollKey={`${pending.actionId}-${rollKey}`}
        >
          {dicePhase !== "settled" ? (
            <div className="roll-suspense">
              {dicePhase === "rolling" ? "骰子疾走中..." : "骰声渐歇..."}
            </div>
          ) : rolled ? (
            <div className="roll-reveal" aria-live="polite">
              <div className={`result ${OUTCOME_TEXT[rolled.outcome]?.cls ?? ""}`}>
                {OUTCOME_TEXT[rolled.outcome]?.text}
              </div>
              <div className="roll-total">
                {rolled.crit === "none"
                  ? `${rolled.dice.join(" + ")} = ${rolled.total} / 需求 ${pending.requiredPoints}`
                  : rolled.crit === "success"
                    ? "天命在手"
                    : "命途急转"}
              </div>
              <FateRail
                total={rolled.total}
                required={pending.requiredPoints}
                crit={rolled.crit}
                tone={tone}
              />
            </div>
          ) : null}
        </DiceStrip>
      )}

      <div className="check-actions">
        {clientRoll ? (
          <span className="reroll-hint">命数正在回响...</span>
        ) : !rolled ? (
          <span className="reroll-hint">骰声未落...</span>
        ) : settlingNow ? (
          <span className="reroll-hint">骰子正在落定...</span>
        ) : (
          <>
            {pending.canReroll && (
              <button className="btn" disabled={busy} onClick={() => rollDice()}>
                {pending.maxRerolls === 1
                  ? `重掷命数（断界仅 1 次，-${pending.rerollCost} 气运）`
                  : `重掷命数（-${pending.rerollCost} 气运）`}
              </button>
            )}
            {rolled.outcome === 1 && !pending.canReroll && (
              <span className="reroll-hint">
                {pending.rerollBlockedReason === "limit"
                  ? `断界锁骰：本检定最多重掷 ${pending.maxRerolls ?? 0} 次`
                  : "气运不足，难以重掷"}
              </span>
            )}
            <button className="btn primary" disabled={busy} onClick={() => cont()}>
              继续
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FateRail({
  total,
  required,
  crit,
  tone,
}: {
  total: number;
  required: number;
  crit: "none" | "success" | "fail";
  tone?: "win" | "lose";
}) {
  const max = Math.max(required, Number.isFinite(total) ? total : required, 1);
  const totalPct = crit === "success" ? 100 : crit === "fail" ? 0 : Math.max(0, Math.min(100, (total / max) * 100));
  const requiredPct = Math.max(0, Math.min(100, (required / max) * 100));
  const style = {
    "--fate-total": `${totalPct}%`,
    "--fate-required": `${requiredPct}%`,
  } as CSSProperties;
  return (
    <div className={`fate-rail ${tone ?? ""} ${crit !== "none" ? `crit-${crit}` : ""}`} style={style} aria-hidden="true">
      <span className="fate-fill" />
      <span className="fate-mark" />
      <b>{resultBadge(crit, tone)}</b>
    </div>
  );
}

function resultBadge(crit: "none" | "success" | "fail", tone?: "win" | "lose"): string {
  if (crit === "success") return "天命破线";
  if (crit === "fail") return "命线崩裂";
  return tone === "win" ? "点数撞线" : "差一步";
}

function PointSummary({ items }: { items: PointBreakdownItem[] }) {
  if (items.length === 0) return null;
  const summary = summarizeBreakdown(items);
  return (
    <div className={`point-summary ${summary.tone}`}>
      <b>{summary.label}</b>
      <span>{summary.detail}</span>
    </div>
  );
}

function PointBreakdown({ items }: { items: PointBreakdownItem[] }) {
  if (items.length === 0) {
    return <div className="point-breakdown empty-inline">此刻没有旧物或修正相助</div>;
  }
  return (
    <div className="point-breakdown">
      {items.map((it) => (
        <div key={it.stat} className="point-row">
          <span>
            {it.stat}
            {sourceSummary(it) ? (
              <small className={`point-source ${sourceSummary(it)?.tone}`}>
                {sourceSummary(it)?.label}：{sourceSummary(it)?.detail}
              </small>
            ) : null}
          </span>
          <code>
            {signed(it.adjustment)} × {it.coefficient}
          </code>
          <b className={it.points >= 0 ? "pos" : "neg"}>{signed(it.points)}</b>
        </div>
      ))}
    </div>
  );
}

function DiceStrip({
  values,
  phase,
  tone,
  rollKey,
  children,
}: {
  values: number[];
  phase: DiePhase;
  tone?: "win" | "lose";
  rollKey: string | number;
  children?: ReactNode;
}) {
  return (
    <div className={`dice-row ${phase}`}>
      {values.map((d, i) => (
        <Die
          key={`${rollKey}-${i}-${d}-${phase}`}
          value={d}
          tone={tone}
          phase={phase}
          variant={i < 2 ? "base" : "luck"}
          index={i}
        />
      ))}
      {children}
    </div>
  );
}

function rollingDiceValues(numDice: number, seed: number): number[] {
  return Array.from({ length: Math.max(1, numDice) }, (_, i) => ((seed + i * 2) % 6) + 1);
}

function ExtraDiceControl({
  extraDice,
  luck,
  currentPoints,
  requiredPoints,
  maxRerolls,
  disabled,
  onChange,
}: {
  extraDice: number;
  luck: number;
  currentPoints: number;
  requiredPoints: number;
  maxRerolls: number | null;
  disabled: boolean;
  onChange: (n: number) => void;
}) {
  const cost = extraDiceCost(extraDice);
  const nextCost = extraDiceCost(extraDice + 1);
  const nextStepCost = nextCost - cost;
  const canAdd = nextCost <= luck;
  const baseProbability = successProbability(currentPoints, requiredPoints, 2);
  const currentProbability = successProbability(currentPoints, requiredPoints, 2 + extraDice);
  const nextProbability = successProbability(currentPoints, requiredPoints, 3 + extraDice);
  const luckAfterSelection = Math.max(0, luck - cost);
  const luckAfterNext = Math.max(0, luck - nextCost);
  const currentRerolls = rerollCapacity(luckAfterSelection, maxRerolls);
  const nextRerolls = rerollCapacity(luckAfterNext, maxRerolls);
  const oddsLabel =
    extraDice > 0
      ? `当前胜率 ${formatPercent(baseProbability)} → ${formatPercent(currentProbability)}；再加 1 颗需 ${nextStepCost}`
      : `加一骰 ${formatPercent(baseProbability)} → ${formatPercent(nextProbability)}；需 ${nextStepCost} 气运`;
  const costLabel =
    extraDice > 0
      ? `总耗 ${cost}，剩 ${luckAfterSelection}；${rerollLine(currentRerolls, maxRerolls)}`
      : canAdd
        ? `总耗 ${nextCost}，剩 ${luckAfterNext}；${rerollLine(nextRerolls, maxRerolls)}`
        : `还差 ${nextCost - luck} 气运才可加骰`;
  return (
    <div className="extra-dice-control">
      <div className="extra-dice-head">
        <span>额外骰子</span>
        <b>{extraDice} 颗</b>
      </div>
      <div className="stepper">
        <button
          className="icon-btn"
          disabled={disabled || extraDice <= 0}
          onClick={() => onChange(extraDice - 1)}
          aria-label="减少额外骰子"
        >
          -
        </button>
        <div className="step-value">
          {2 + extraDice}d6
          <small>消耗 {cost} 气运</small>
        </div>
        <button
          className="icon-btn"
          disabled={disabled || !canAdd}
          onClick={() => onChange(extraDice + 1)}
          aria-label="增加额外骰子"
        >
          +
        </button>
      </div>
      <div className={`dice-cost-preview ${canAdd || extraDice > 0 ? "ready" : "short"}`}>
        <span>{oddsLabel}</span>
        <small>{costLabel}</small>
      </div>
    </div>
  );
}

function signed(n: number): string {
  return n >= 0 ? `+${n}` : String(n);
}

function formatPercent(n: number): string {
  return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`;
}

function rerollCapacity(luck: number, maxRerolls: number | null): number {
  let count = 0;
  let spent = 0;
  while (spent + rerollCost(count + 1) <= luck) {
    count++;
    spent += rerollCost(count);
  }
  return maxRerolls == null ? count : Math.min(count, maxRerolls);
}

function rerollLine(count: number, maxRerolls: number | null): string {
  if (count <= 0) return "失败后不能重掷";
  if (maxRerolls === 1) return "断界最多保留 1 次重掷";
  if (count === 1) return "仍可重掷 1 次";
  return `仍可连续重掷 ${count} 次`;
}

function summarizeBreakdown(items: PointBreakdownItem[]): {
  label: string;
  detail: string;
  tone: "help" | "harm" | "mixed" | "plain";
} {
  const total = items.reduce((sum, item) => sum + item.points, 0);
  const sourcePoints = items.flatMap(pointSourceContributions);
  const relicHelp = sourcePoints
    .filter((source) => source.points > 0)
    .reduce((sum, source) => sum + source.points, 0);
  const relicHarm = sourcePoints
    .filter((source) => source.points < 0)
    .reduce((sum, source) => sum + source.points, 0);

  if (relicHelp > 0 && relicHarm < 0) {
    return {
      label: "旧物交织",
      detail: `${signed(relicHelp)} / ${signed(relicHarm)}，合计修正 ${signed(total)}`,
      tone: "mixed",
    };
  }
  if (relicHelp > 0) {
    return {
      label: "旧物牵动",
      detail: `${signed(relicHelp)}，合计修正 ${signed(total)}`,
      tone: "help",
    };
  }
  if (relicHarm < 0) {
    return {
      label: "旧物牵动",
      detail: `${signed(relicHarm)}，合计修正 ${signed(total)}`,
      tone: "harm",
    };
  }
  return {
    label: "属性修正",
    detail: `合计 ${signed(total)}`,
    tone: total > 0 ? "help" : total < 0 ? "harm" : "plain",
  };
}

function sourceSummary(item: PointBreakdownItem): {
  label: string;
  detail: string;
  tone: "help" | "harm" | "mixed";
} | null {
  const contributions = pointSourceContributions(item);
  if (contributions.length === 0) return null;
  const help = contributions.filter((source) => source.points > 0);
  const harm = contributions.filter((source) => source.points < 0);
  const tone = help.length && harm.length ? "mixed" : harm.length ? "harm" : "help";
  return {
    label: tone === "mixed" ? "旧物交织" : "旧物牵动",
    detail: formatSourceContributions(contributions),
    tone,
  };
}

function pointSourceContributions(item: PointBreakdownItem): Array<{ label: string; points: number }> {
  if (item.sourceContributions?.length) {
    return item.sourceContributions
      .filter((source) => source.points !== 0)
      .map((source) => ({
        label: source.sourceLabel,
        points: source.points,
      }));
  }
  if (!item.sourceLabels?.length || item.points === 0) return [];
  return [{ label: formatSources(item.sourceLabels), points: item.points }];
}

function formatSourceContributions(contributions: Array<{ label: string; points: number }>): string {
  const compact = contributions.slice(0, 2).map((source) => `${source.label} ${signed(source.points)}`);
  return contributions.length > 2 ? `${compact.join(" / ")} 等` : compact.join(" / ");
}

function formatSources(labels: string[]): string {
  const unique = [...new Set(labels)].slice(0, 2);
  return labels.length > 2 ? `${unique.join(" / ")} 等` : unique.join(" / ");
}
